import { Button, Flex, Spacer } from "@chakra-ui/react";
import React from "react";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import {
  buildRuleImportErrors,
  RuleImportError,
  zodIssuesToRuleImportErrors,
} from "./helpers";
import {
  RuleImportExportJsonSchema,
  RuleImportExportSchema,
} from "./ruleSchema";

type Props = {
  formTemplateId: string;
  onBack: () => void;
  onComplete: (errors: RuleImportError[]) => void;
};

export const ImportRulesFromJSON: React.FC<Props> = ({
  formTemplateId,
  onBack,
  onComplete,
}) => {
  const toast = useAvelaToast();
  const [json, setJson] = React.useState("");
  const [isImporting, setImporting] = React.useState(false);
  const service = FSQ.useImportRuleConfiguration();

  const handleImport = React.useCallback(async () => {
    setImporting(true);
    toast.closeAll();

    try {
      if (json.trim() === "") {
        toast.error({
          title: "JSON is required",
          description: "Please provide valid JSON to import",
        });
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        toast.error({
          title: "Invalid JSON",
          description: "Please check your JSON format",
        });
        return;
      }

      // Validate against the same schema the backend enforces, so structural
      // and cross-rule errors surface here instead of a generic 400.
      const validation = RuleImportExportSchema.safeParse(parsed);
      if (!validation.success) {
        toast({
          title: "Some rules were not imported",
          description: "Review the specific errors and try again",
          status: "warning",
        });
        return onComplete(
          zodIssuesToRuleImportErrors(
            validation.error.issues,
            Array.isArray(parsed) ? parsed : []
          )
        );
      }

      const { data: result } = await service.mutateAsync({
        path: { formTemplateId },
        body: validation.data as FSR.ImportRuleConfigurationData["body"],
      });
      const response = result?.data;

      if (!response) {
        toast.error({
          title: "Something went wrong",
          description: "Please try again",
        });
        return;
      }

      if (response.result === "invalid") {
        toast({
          title: "Some rules were not imported",
          description: "Review the specific errors and try again",
          status: "warning",
        });
        return onComplete(
          buildRuleImportErrors(response.errorGroups, validation.data)
        );
      }

      toast({
        title: "Imported from JSON",
        description: "Rules imported successfully",
        status: "info",
        isClosable: true,
      });
      return onComplete([]);
    } catch (error: any) {
      if (error?.error?.message) {
        toast.error({
          title: "Something went wrong",
          description: error.error.message,
        });
      } else {
        toast.genericError();
      }
    } finally {
      setImporting(false);
    }
  }, [json, service, formTemplateId, toast, onComplete]);

  return (
    <Flex direction="column" gap={6} grow="1" height="100%">
      <JsonEditor
        schema={RuleImportExportJsonSchema}
        text={json}
        onChange={setJson}
        filename="ruleConfiguration.json"
        editorHeight="fillParent"
      />
      <AdminFormButtons insideTabPanel>
        <Button variant="outline" colorScheme="gray" onClick={onBack}>
          Back
        </Button>
        <Spacer />
        <Button isLoading={isImporting} onClick={handleImport}>
          Import
        </Button>
      </AdminFormButtons>
    </Flex>
  );
};
